import React,{useState,useEffect} from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiCheckCircle,FiCircle,FiBook,FiUsers,FiCalendar,FiTarget,FiArrowRight,FiDownload,FiEdit3,FiSave,FiArrowLeft}=FiIcons;

function BookPlanningChecklist() {
  const [checkedItems,setCheckedItems]=useState({});
  const [responses,setResponses]=useState({});
  const [activeItem,setActiveItem]=useState(null);

  // Load data from localStorage on component mount
  useEffect(()=> {
    const savedCheckedItems=localStorage.getItem('bookPlanningChecked');
    const savedResponses=localStorage.getItem('bookPlanningResponses');
    if (savedCheckedItems) {
      setCheckedItems(JSON.parse(savedCheckedItems));
    }
    if (savedResponses) {
      setResponses(JSON.parse(savedResponses));
    }
  },[]);

  // Save to localStorage whenever data changes
  useEffect(()=> {
    localStorage.setItem('bookPlanningChecked',JSON.stringify(checkedItems));
  },[checkedItems]);

  useEffect(()=> {
    localStorage.setItem('bookPlanningResponses',JSON.stringify(responses));
  },[responses]);

  const checklistItems=[
    {
      id: 'book-type',
      title: 'Choose Your Book Type',
      description: 'Decide between fiction or nonfiction',
      category: 'foundation',
      prompt: 'What type of book are you planning to write?',
      options: ['Fiction','Nonfiction'],
      helpText: 'Fiction tells stories that entertain and inspire through narrative. Nonfiction teaches,encourages,or equips readers with practical wisdom.'
    },
    {
      id: 'audience',
      title: 'Define Your Audience',
      description: 'Who specifically is this book for?',
      category: 'foundation',
      prompt: 'Describe your ideal reader in detail:',
      type: 'textarea',
      helpText: 'Think about age,life situation,struggles,interests. The more specific,the better. Example: "Christian mothers in their 30s struggling to balance work and family while maintaining their faith."'
    },
    {
      id: 'message',
      title: 'Clarify Your Core Message',
      description: 'What transformation will readers experience?',
      category: 'foundation',
      prompt: 'Complete this sentence: "After reading my book,readers will..."',
      type: 'textarea',
      helpText: 'Focus on the transformation or outcome. What specific change,insight,or benefit will readers gain?'
    },
    {
      id: 'word-count',
      title: 'Estimate Word Count',
      description: 'Set a realistic target for your book length',
      category: 'planning',
      prompt: 'What is your target word count?',
      type: 'select',
      options: [
        '30,000-40,000 words (Short book/Novella)',
        '50,000-70,000 words (Standard nonfiction)',
        '70,000-90,000 words (Full-length book)',
        '90,000+ words (Comprehensive/Academic)'
      ],
      helpText: 'Consider your genre and audience. Most nonfiction books are 50,000-70,000 words. Fiction varies widely but 70,000-90,000 is common for novels.'
    },
    {
      id: 'writing-days',
      title: 'Choose Writing Days',
      description: 'Which days of the week will you write?',
      category: 'planning',
      prompt: 'Select your writing days:',
      type: 'multiselect',
      options: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
      helpText: 'Consistency matters more than quantity. Choose days you can realistically commit to.'
    },
    {
      id: 'timeframe',
      title: 'Set Your Timeframe',
      description: 'When do you want to complete your first draft?',
      category: 'planning',
      prompt: 'When do you want to complete your first draft?',
      type: 'date',
      helpText: 'Be realistic but ambitious. Most first drafts take 3-12 months depending on length and writing frequency.'
    },
    {
      id: 'milestone',
      title: 'Set First Milestone',
      description: 'What will you complete in the next 30 days?',
      category: 'action',
      prompt: 'What specific milestone will you achieve in the next 30 days?',
      type: 'textarea',
      helpText: 'Make it specific and measurable. Examples: "Complete detailed outline," "Write first 3 chapters," "Research and interview 5 sources."'
    }
  ];

  const categories={
    foundation: {title: 'Foundation',icon: FiBook},
    planning: {title: 'Planning',icon: FiCalendar},
    action: {title: 'Action Steps',icon: FiTarget}
  };

  // Helper function to check if a response has content
  const hasValidResponse=(response)=> {
    if (!response) return false;
    if (Array.isArray(response)) return response.length > 0;
    if (typeof response==='string') return response.trim() !=='';
    return true;
  };

  const toggleItem=(id)=> {
    const response=responses[id];
    if (hasValidResponse(response)) {
      setCheckedItems(prev=> ({...prev,[id]: !prev[id]}));
    }
  };

  const handleResponse=(id,value)=> {
    setResponses(prev=> ({...prev,[id]: value}));
    // Auto-check item when response is provided
    if (hasValidResponse(value)) {
      setCheckedItems(prev=> ({...prev,[id]: true}));
    } else {
      // Uncheck if response is cleared
      setCheckedItems(prev=> ({...prev,[id]: false}));
    }
  };

  const handleMultiSelectResponse=(id,option)=> {
    const currentValue=responses[id] || [];
    const newValue=currentValue.includes(option) 
      ? currentValue.filter(item=> item !==option) 
      : [...currentValue,option];
    handleResponse(id,newValue);
  };

  const exportPlan=()=> {
    const completedItems=checklistItems.filter(item=> checkedItems[item.id]);
    const planData={
      generatedDate: new Date().toLocaleDateString(),
      completedItems: completedItems.length,
      totalItems: checklistItems.length,
      responses: {}
    };

    // Include responses for completed items
    completedItems.forEach(item=> {
      planData.responses[item.title]=responses[item.id];
    });

    // Create downloadable content
    let content=`MY BOOK PLANNING SUMMARY\n`;
    content +=`Generated: ${planData.generatedDate}\n`;
    content +=`Progress: ${planData.completedItems}/${planData.totalItems} items completed\n\n`;

    completedItems.forEach(item=> {
      content +=`${item.title.toUpperCase()}\n`;
      const response=responses[item.id];
      if (Array.isArray(response)) {
        content +=`${response.join(',')}\n\n`;
      } else {
        content +=`${response}\n\n`;
      }
    });

    content +=`\n---\nGenerated by Start Here: A Mini Planner for Christian Authors\n`;
    content +=`Continue your journey at [Your Website URL]`;

    // Create and download file
    const blob=new Blob([content],{type: 'text/plain'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url;
    a.download='my-book-plan.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderInput=(item)=> {
    const value=responses[item.id] || '';
    switch (item.type) {
      case 'textarea':
        return (
          <textarea 
            value={value}
            onChange={(e)=> handleResponse(item.id,e.target.value)}
            placeholder={`${item.prompt}`}
            className="w-full p-4 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-colors h-32 resize-none"
          />
        );
      case 'select':
        return (
          <select 
            value={value}
            onChange={(e)=> handleResponse(item.id,e.target.value)}
            className="w-full p-4 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-colors"
          >
            <option value="">Select an option...</option>
            {item.options.map((option,index)=> (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );
      case 'multiselect':
        const selectedValues=Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {item.options.map((option,index)=> (
              <label key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <input 
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={()=> handleMultiSelectResponse(item.id,option)}
                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                />
                <span className="text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );
      case 'date':
        return (
          <input 
            type="date"
            value={value}
            onChange={(e)=> handleResponse(item.id,e.target.value)}
            className="w-full p-4 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-colors"
          />
        );
      default:
        return (
          <input 
            type="text"
            value={value}
            onChange={(e)=> handleResponse(item.id,e.target.value)}
            placeholder={item.prompt}
            className="w-full p-4 border border-gray-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-colors"
          />
        );
    }
  };

  const completedCount=Object.values(checkedItems).filter(Boolean).length;
  const progressPercentage=(completedCount / checklistItems.length) * 100;

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-serif text-4xl text-gray-800 mb-4">
          Book Planning Checklist
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Turn your book idea into a concrete plan. 
          <span className="text-emerald-700 font-medium block mt-2">
            Small steps lead to finished books.
          </span>
        </p>
      </div>

      {/* Progress */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-amber-100 mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-gray-800">Your Progress</h3>
          <span className="text-sm text-gray-600">
            {completedCount} of {checklistItems.length} completed
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-emerald-600 h-3 rounded-full transition-all duration-500" 
            style={{width: `${progressPercentage}%`}}
          ></div>
        </div>
        <p className="text-sm text-emerald-700 mt-2 font-medium">
          {progressPercentage===100 
            ? "🎉 Congratulations! You're ready to start writing!" 
            : progressPercentage >=50 
            ? "Great progress! You're over halfway there." 
            : "Keep going! Each step brings clarity."}
        </p>
        {completedCount > 0 && (
          <div className="mt-4 pt-4 border-t border-amber-200">
            <button 
              onClick={exportPlan}
              className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-2 text-sm"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              <span>Export My Plan</span>
            </button>
          </div>
        )}
      </div>

      {/* Checklist Sections */}
      {Object.entries(categories).map(([categoryKey,category])=> {
        const categoryItems=checklistItems.filter(item=> item.category===categoryKey);
        return (
          <div key={categoryKey} className="mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <SafeIcon icon={category.icon} className="w-6 h-6 text-emerald-700" />
              <h2 className="font-serif text-2xl text-gray-800">{category.title}</h2>
            </div>
            <div className="space-y-6">
              {categoryItems.map((item)=> (
                <div 
                  key={item.id} 
                  className={`bg-white/80 backdrop-blur-sm rounded-2xl border transition-all ${
                    checkedItems[item.id] 
                      ? 'border-emerald-300 bg-emerald-50/30' 
                      : 'border-gray-200'
                  }`}
                >
                  {/* Item Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-start space-x-4">
                      <button 
                        onClick={()=> toggleItem(item.id)}
                        className="mt-1 flex-shrink-0"
                        disabled={!hasValidResponse(responses[item.id])}
                      >
                        <SafeIcon 
                          icon={checkedItems[item.id] ? FiCheckCircle : FiCircle} 
                          className={`w-6 h-6 transition-colors ${
                            checkedItems[item.id] 
                              ? 'text-emerald-600' 
                              : hasValidResponse(responses[item.id]) 
                              ? 'text-emerald-400 hover:text-emerald-600' 
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                      <div className="flex-1">
                        <h3 className={`font-semibold text-lg mb-2 transition-colors ${
                          checkedItems[item.id] ? 'text-emerald-800' : 'text-gray-800'
                        }`}>
                          {item.title}
                        </h3>
                        <p className="text-gray-600 mb-4">
                          {item.description}
                        </p>
                        <button 
                          onClick={()=> setActiveItem(activeItem===item.id ? null : item.id)}
                          className="flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 transition-colors"
                        >
                          <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            {hasValidResponse(responses[item.id]) ? 'Edit Response' : 'Add Details'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Input Section */}
                  {activeItem===item.id && (
                    <div className="p-6 bg-gray-50/50">
                      <h4 className="font-semibold text-gray-800 mb-3">
                        {item.prompt}
                      </h4>

                      {/* Special handling for book type */}
                      {item.id==='book-type' ? (
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          {item.options.map((option,index)=> (
                            <button 
                              key={index}
                              onClick={()=> handleResponse(item.id,option)}
                              className={`p-4 rounded-xl border transition-all text-left ${
                                responses[item.id]===option 
                                  ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                                  : 'border-gray-300 bg-white hover:border-emerald-300 hover:shadow-sm'
                              }`}
                            >
                              <h5 className={`font-semibold mb-2 ${
                                responses[item.id]===option ? 'text-emerald-700' : 'text-gray-700'
                              }`}>
                                {option}
                              </h5>
                              <p className="text-gray-600 text-sm">
                                {option==='Fiction' 
                                  ? 'Stories that entertain,inspire,and reveal truth through narrative' 
                                  : 'Teaching,encouraging,or equipping readers with practical wisdom'}
                              </p>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="mb-4">
                          {renderInput(item)}
                        </div>
                      )}

                      <p className="text-sm text-gray-500 mb-4">
                        💡 {item.helpText}
                      </p>

                      <div className="flex items-center justify-between">
                        <button 
                          onClick={()=> setActiveItem(null)}
                          className="text-gray-600 hover:text-gray-800 transition-colors text-sm"
                        >
                          Close
                        </button>
                        {hasValidResponse(responses[item.id]) && (
                          <div className="flex items-center space-x-2 text-emerald-700 text-sm">
                            <SafeIcon icon={FiSave} className="w-4 h-4" />
                            <span>Saved automatically</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Show Response Preview */}
                  {hasValidResponse(responses[item.id]) && activeItem !==item.id && (
                    <div className="p-4 bg-emerald-50/50 border-t border-emerald-100">
                      <p className="text-sm text-emerald-800 font-medium mb-1">Your response:</p>
                      <p className="text-sm text-gray-700">
                        {Array.isArray(responses[item.id]) 
                          ? responses[item.id].join(',') 
                          : responses[item.id].length > 100 
                          ? `${responses[item.id].substring(0,100)}...` 
                          : responses[item.id]}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Summary & Next Steps */}
      {progressPercentage===100 && (
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-8 border border-emerald-200 mb-8">
          <div className="text-center mb-6">
            <SafeIcon icon={FiCheckCircle} className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-gray-800 mb-2">
              🎉 Your Book Plan is Complete!
            </h3>
            <p className="text-gray-600 leading-relaxed">
              You've laid a solid foundation for your book. Here's a summary of your plan:
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white/80 rounded-xl p-4">
              <h4 className="font-semibold text-emerald-700 mb-2">Book Type</h4>
              <p className="text-gray-700">{responses['book-type']}</p>
            </div>
            <div className="bg-white/80 rounded-xl p-4">
              <h4 className="font-semibold text-emerald-700 mb-2">Target Length</h4>
              <p className="text-gray-700">{responses['word-count']}</p>
            </div>
            <div className="bg-white/80 rounded-xl p-4">
              <h4 className="font-semibold text-emerald-700 mb-2">Writing Days</h4>
              <p className="text-gray-700">
                {Array.isArray(responses['writing-days']) 
                  ? responses['writing-days'].join(',') 
                  : responses['writing-days']}
              </p>
            </div>
            <div className="bg-white/80 rounded-xl p-4">
              <h4 className="font-semibold text-emerald-700 mb-2">Target Completion</h4>
              <p className="text-gray-700">
                {responses['timeframe'] 
                  ? new Date(responses['timeframe']).toLocaleDateString() 
                  : 'Not set'}
              </p>
            </div>
          </div>
          <div className="text-center">
            <button 
              onClick={exportPlan}
              className="bg-emerald-600 text-white px-8 py-3 rounded-xl hover:bg-emerald-700 transition-colors flex items-center justify-center space-x-2 mx-auto mb-4"
            >
              <SafeIcon icon={FiDownload} className="w-5 h-5" />
              <span className="font-medium">Download Complete Plan</span>
            </button>
            <p className="text-sm text-gray-600">
              Your plan is saved automatically and ready to guide your writing journey.
            </p>
          </div>
        </div>
      )}

      {/* Encouragement & Next Steps */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-amber-100 text-center mb-8">
        <h3 className="font-serif text-2xl text-gray-800 mb-4">
          {progressPercentage===100 ? "You're Ready to Write!" : "Keep Building Your Plan"}
        </h3>
        <p className="text-gray-600 mb-6 leading-relaxed">
          {progressPercentage===100 
            ? "You've created a solid foundation for your book. Now it's time to start writing with confidence and clarity."
            : "Every detail you add brings more clarity to your book. Take your time—each step is building toward your finished book."}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {progressPercentage===100 && (
            <Link 
              to="/next-steps"
              className="bg-emerald-600 text-white px-6 py-3 rounded-xl hover:bg-emerald-700 transition-colors flex items-center justify-center space-x-2"
            >
              <span>Ready for Next Steps</span>
              <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
            </Link>
          )}
          <Link 
            to="/next-steps"
            className="bg-amber-600 text-white px-6 py-3 rounded-xl hover:bg-amber-700 transition-colors flex items-center justify-center space-x-2"
          >
            <span>Learn About Your Next Steps</span>
            <SafeIcon icon={FiUsers} className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Navigation Back */}
      <div className="text-center">
        <Link 
          to="/" 
          className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>
      </div>
    </div>
  );
}

export default BookPlanningChecklist;